import { Box, Stack } from '@mui/joy';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import partFinderNoAttributes from 'assets/partFinderNoAttributes.svg';
import { useGetPartFinderAttributes } from 'companySettings/hooks';
import { AgGridTable, Button, PageTitle } from 'core/components';
import { TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider } from 'core/states';
import { Color, Size } from 'theme/enums';

import {
  DownloadPartFinderAttributesButton,
  EditPartFinderAttributeButton,
  PublishPartFinderAttributesButton,
  RemovePartFinderAttributesButton,
} from './actions';
import { getColumnDefs } from './columns';
import { UpsertPartFinderAttributeButton } from './UpsertPartFinderAttribute';
import { PartFinderEmptyState } from '../PartFinderEmptyState';

const TABLE_MIN_HEIGHT = 320;

// Narrower than the grid's default minimum, so the columns can be dragged small.
const DEFAULT_COL_DEF = { minWidth: 40 };

export const PartFinderAttributes = () => {
  const { t } = useTranslation();

  const { records, isLoading, loadingPercentage, currentPage } = useGetPartFinderAttributes();

  const columnDefs = useMemo(() => getColumnDefs(), []);

  const actionButtons = useCallback(
    () => [<UpsertPartFinderAttributeButton key="addPartFinderAttributeButton" />],
    []
  );

  // Every row update clears the selection, so the selected rows are always the latest ones.
  const selectionActionButtons = useMemo(
    () => [
      <EditPartFinderAttributeButton key="editPartFinderAttributeButton" />,
      <PublishPartFinderAttributesButton key="publishPartFinderAttributesButton" />,
      <DownloadPartFinderAttributesButton key="downloadPartFinderAttributesButton" />,
      <RemovePartFinderAttributesButton key="removePartFinderAttributesButton" />,
    ],
    []
  );

  const emptyStateElement = useMemo(
    () => (
      <PartFinderEmptyState
        image={partFinderNoAttributes}
        message={t('messagePartFinderNoAttributes')}
        action={
          <UpsertPartFinderAttributeButton
            buttonElement={
              <Button
                text={t('buttonLabelAddPartFinderAttribute')}
                color={Color.SUCCESS}
                size={Size.MD}
              />
            }
          />
        }
      />
    ),
    [t]
  );

  return (
    <Stack sx={{ height: '100%' }}>
      <PageTitle title={t('titlePartFinderAttributes')} />

      {/* CompanySettingsLayout gives its child a definite height, so the grid takes what is left
          and its footer stays in view; the floor keeps it usable on short viewports. */}
      <Box sx={{ flexGrow: 1, minHeight: TABLE_MIN_HEIGHT, overflow: 'hidden' }}>
        <AgGridClientTableProvider
          tableId="partFinderAttributes"
          entityType="ValueList"
          columnDefs={columnDefs}
          data={records}
          isDataLoading={isLoading}
          loadingPercentage={loadingPercentage}
          dataCurrentPage={currentPage}
          layoutType={TableLayoutType.PART_FINDER_ATTRIBUTE}
          hasLayoutActions
          headerCheckbox
          defaultColDef={DEFAULT_COL_DEF}
        >
          <AgGridTable
            actionButtons={actionButtons}
            selectionActionButtons={selectionActionButtons}
            emptyStateElement={emptyStateElement}
            hideTopActionsWhenEmpty
          />
        </AgGridClientTableProvider>
      </Box>
    </Stack>
  );
};
